export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  content: string;
  author: {
    name: string;
    image: string;
    bio: string;
  };
  publishedAt: string;
  updatedAt?: string;
  tags: string[];
  category: string;
  readingTime: number;
  wordCount: number;
  featured: boolean;
  image?: string;
  seoKeywords: string[];
  excerpt?: string;
  views?: number;
  likes?: number;
}

export interface BlogMetadata {
  title: string;
  description: string;
  publishedAt: string;
  updatedAt?: string;
  tags: string[];
  category: string;
  featured?: boolean;
  image?: string;
  seoKeywords: string[];
}

// Calculate reading time based on word count
export function calculateReadingTime(content: string): number {
  const wordsPerMinute = 200; // Average reading speed
  const wordCount = countWords(content);
  return Math.ceil(wordCount / wordsPerMinute);
}

// Count words in content
export function countWords(content: string): number {
  // Remove markdown syntax and count words
  const plainText = content
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`[^`]*`/g, '') // Remove inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '') // Remove images
    .replace(/\[[^\]]*\]\([^)]*\)/g, '') // Remove links
    .replace(/[#*_~`]/g, '') // Remove markdown formatting
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
  
  return plainText.split(' ').filter(word => word.length > 0).length;
}

// Generate slug from title
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Remove duplicate hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

// Format date for display
export function formatDate(dateString: string, language: string = 'en'): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(language.startsWith('ar') ? 'ar-EG' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Convert number to Arabic numerals
export function toArabicNumerals(num: number): string {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return num.toString().split('').map(digit => arabicNumerals[parseInt(digit)]).join('');
}

// Get estimated reading time text
export function getReadingTimeText(minutes: number, language: string = 'en'): string {
  if (language === 'ar') {
    const arabicMinutes = toArabicNumerals(minutes);
    if (minutes < 1) return '< ١ دقيقة قراءة';
    if (minutes === 1) return '١ دقيقة قراءة';
    return `${arabicMinutes} دقيقة قراءة`;
  }
  
  if (minutes < 1) return '< 1 min read';
  if (minutes === 1) return '1 min read';
  return `${minutes} min read`;
}

// Default author information
export const defaultAuthor = {
  name: 'Talal Alkhaled',
  image: '/optimized/Talal-md.webp',
  bio: 'Computer Engineer from Michigan State University specializing in AI, web development, and mobile applications.'
};

// Blog categories
export const blogCategories = {
  AI: 'Artificial Intelligence',
  WEB: 'Web Development',
  MOBILE: 'Mobile Development',
  SECURITY: 'Web Security',
  PERFORMANCE: 'Performance Optimization',
  TUTORIAL: 'Tutorial',
  CASE_STUDY: 'Case Study',
  TIPS: 'Tips & Tricks'
};

// SEO-optimized excerpt generation
export function generateExcerpt(content: string, maxLength = 155): string {
  // Remove markdown and get plain text
  const plainText = content
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/`[^`]*`/g, '') // Remove inline code
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '') // Remove images
    .replace(/\[[^\]]*\]\([^)]*\)/g, '') // Remove links
    .replace(/[#*_~`]/g, '') // Remove markdown formatting
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  if (plainText.length <= maxLength) {
    return plainText;
  }

  // Find the last complete sentence within the limit
  const truncated = plainText.substring(0, maxLength);
  const lastSentenceEnd = Math.max(
    truncated.lastIndexOf('.'),
    truncated.lastIndexOf('!'),
    truncated.lastIndexOf('?')
  );

  if (lastSentenceEnd > maxLength * 0.6) {
    return truncated.substring(0, lastSentenceEnd + 1);
  }

  // If no good sentence break, truncate at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastSpace) + '...';
}

// Generate SEO-friendly meta description
export function generateMetaDescription(post: BlogPost): string {
  const excerpt = generateExcerpt(post.content, 140);
  return `${excerpt} | ${post.readingTime} min read by ${post.author.name}`;
}

// Generate article keywords
export function generateArticleKeywords(post: BlogPost): string {
  const baseKeywords = [
    'Talal Alkhaled',
    'Computer Engineering',
    'Michigan State University',
    'Technical Blog'
  ];
  
  return [...baseKeywords, ...post.seoKeywords, ...post.tags].join(', ');
} 